import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button, SegmentedControl } from '@shared/design-system/ui'
import { inviteTokenFromInput } from '@entities/partnership'
import { cx } from '@shared/lib/cx'
import { MarketplacePanel } from './MarketplacePanel'
import { MyAffiliationsPanel } from './MyAffiliationsPanel'
import { useAffiliateByInviteMutation } from '../api/affiliate.mutations'
import { affiliationRequestErrorFeedback } from '../lib/affiliation-request-error-feedback'
import {
  header,
  headerText,
  inviteNotice,
  inviteNoticeContent,
  inviteNoticeDescription,
  inviteNoticeIcon,
  inviteNoticeIconTone,
  inviteNoticeText,
  inviteNoticeTitle,
  page,
  subtitle,
  title,
} from './affiliates.css'

type AffiliatesTab = 'marketplace' | 'mine'

const tabs = [
  { value: 'marketplace' as const, label: 'Marketplace' },
  { value: 'mine' as const, label: 'Minhas afiliações' },
]

type InviteFeedback = {
  tone: 'loading' | 'success' | 'danger'
  title: string
  description: string
}

export function AffiliatesScreen({ inviteToken }: { inviteToken?: string }) {
  const [tab, setTab] = useState<AffiliatesTab>('marketplace')
  const [inviteFeedback, setInviteFeedback] = useState<InviteFeedback | null>(null)
  const consumedInviteToken = useRef<string | undefined>(undefined)
  const byInvite = useAffiliateByInviteMutation()

  useEffect(() => {
    const token = inviteTokenFromInput(inviteToken ?? '')
    if (!token || consumedInviteToken.current === token) return

    consumedInviteToken.current = token
    setInviteFeedback({
      tone: 'loading',
      title: 'Validando convite',
      description: 'Estamos conferindo o produto e vinculando sua conta como afiliado.',
    })

    void byInvite.mutateAsync(token).then(
      (agreement) => {
        const approved = agreement.status === 'APROVADO'
        setInviteFeedback({
          tone: 'success',
          title: approved ? 'Convite aceito' : 'Solicitação enviada',
          description: approved
            ? 'Sua afiliação está aprovada. Copie seu link de venda em Minhas afiliações.'
            : 'O produtor precisa aprovar sua solicitação antes dos links de venda serem liberados.',
        })
        setTab('mine')
      },
      (error: unknown) => {
        const feedback = affiliationRequestErrorFeedback(error, 'invite')
        setInviteFeedback({
          tone: 'danger',
          title: feedback.title,
          description: feedback.description,
        })
      },
    )
  }, [byInvite, inviteToken])

  const InviteIcon =
    inviteFeedback?.tone === 'success'
      ? CheckCircle2
      : inviteFeedback?.tone === 'danger'
        ? AlertTriangle
        : Loader2

  return (
    <div className={page} data-testid="affiliates-screen">
      <div className={header}>
        <div className={headerText}>
          <span className={title}>Afiliados</span>
          <span className={subtitle}>
            Divulgue produtos de outros produtores e ganhe comissão por venda.
          </span>
        </div>
        <SegmentedControl
          value={tab}
          options={tabs}
          onChange={setTab}
          ariaLabel="Seções de afiliados"
        />
      </div>

      {inviteFeedback ? (
        <div className={inviteNotice}>
          <div className={inviteNoticeContent}>
            <span className={cx(inviteNoticeIcon, inviteNoticeIconTone[inviteFeedback.tone])}>
              <InviteIcon size={18} />
            </span>
            <span className={inviteNoticeText}>
              <span className={inviteNoticeTitle}>{inviteFeedback.title}</span>
              <span className={inviteNoticeDescription}>{inviteFeedback.description}</span>
            </span>
          </div>
          {inviteFeedback.tone === 'success' && tab !== 'mine' ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setTab('mine')
              }}
            >
              Ver minhas afiliações
            </Button>
          ) : null}
        </div>
      ) : null}

      {tab === 'marketplace' ? (
        <MarketplacePanel onInviteAccepted={() => setTab('mine')} />
      ) : (
        <MyAffiliationsPanel />
      )}
    </div>
  )
}
